import { useEffect, useState } from 'react';
import Page from '@/components/Page';
import {
  Button, Container, Grid, TextInput, Text, Center, Select,
  Badge, Group,
} from '@mantine/core';
import validate from '@/helpers/validations';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import routes from '@/routes';
import { IconCreditCard } from '@tabler/icons-react';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { getMutationResult } from '@/helpers/query';
import { useCreateOrUpdateMutation, useFinishProcessMutation } from '@/services/api';
import classes from './pay.module.css';

const Pay = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { routeType, id } = useSelector((state) => state.offer);
  const [apiFinishProcess] = useFinishProcessMutation();
  const [apiCreateOrUpdate] = useCreateOrUpdateMutation();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const months = Array.from({ length: 12 }, (_, i) => {
    const val = (i + 1).toString().padStart(2, '0');
    return { value: val, label: val };
  });

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 7 }, (_, i) => {
    const year = currentYear + i;
    return { value: year.toString(), label: year.toString() };
  });

  const required = t('globals.validations.required');
  const uid = t('globals.validations.uid');
  const characters = t('globals.validations.characters');
  const numbers = t('globals.validations.number');

  const formValidations = {
    cardNumber: (value) => (/^\d{16}$/.test(value) ? null : 'מספר כרטיס חייב להכיל 16 ספרות'),
    cardHolderId: (value) => validate(value, { required, numbers, uid }),
    cardHolderName: (value) => validate(value, { required, characters }),
    expiryMonth: (value) => validate(value, { required, numbers }),
    expiryYear: (value) => validate(value, { required, numbers }),
    cvv: (value) => validate(value, { required, numbers }),
  };

  const form = useForm({
    initialValues: {
      cardNumber: '',
      cardHolderId: '',
      cardHolderName: '',
      expiryMonth: '',
      expiryYear: '',
      cvv: '',
    },
    validate: formValidations,
  });

  const handleContinue = () => {
    // Here you can add validation or form submission logic
    console.log(form.values); // Log form values
    navigate(routes.success.path);
  };

  const handleBack = () => {
    navigate(routes.agree.path);
  };

  const option = {
    steps: routeType?.steps,
    total: routeType?.totalPrice,
  };

  // TODO: This function should be triggered after successful payment
  const finishProcess = async () => {
    setLoading(true);
    try {
      const summaryData = {
        id,
      };

      const { data } = await getMutationResult(summaryData, apiFinishProcess);

      if (data.success) {
        const updateOfferObj = {
          id,
          status: 'close',
        };

        await getMutationResult(updateOfferObj, apiCreateOrUpdate);

        handleContinue();
      } else {
        notifications.show({
          title: t('globals.error.title'),
          message: t('globals.error.message'),
        });
      }
    } catch (error) {
      notifications.show({
        title: t('globals.error.title'),
        message: t('globals.error.message'),
      });
    } finally {
      setLoading(false);
    }
  };

  const actions = (
    <>
      <Button
        onClick={finishProcess}
        loading={loading}
        pr={'3rem'}
        pl={'3rem'}
        type="submit"
      >
        לתשלום
      </Button>
    </>
  );
  const title = <Badge
    size="xl"
    variant="filled"
    color={'#cce0f8'}
    className={classes.headerBadge}
    mb={'1rem'}
    styles={{ label: { color: '#0464a0' } }}
  >
    <Group gap="xs" align="center">
      <IconCreditCard size={20} className={classes.headerIcon} />
      <span>אמצעי תשלום</span>
    </Group>
  </Badge>;
  return (
    <Page
      sideSize="xs"
      title={title}
      actions={actions}
    >

      <Container
        mb={'2rem'}
        bg={'white'}
        classNames={{ root: classes.container }}
      >
        <Center>
          <Text fw="bold" fz={'lg'} m={'1rem'}>
            סה״כ ₪{routeType?.totalPrice} (כולל מע״מ)
          </Text>
        </Center>
      </Container>

      <form onSubmit={form.onSubmit(() => finishProcess())} dir="rtl">
        <Grid>
          <Grid.Col span={6} p={'0.5rem'}>
            <TextInput
              label="מספר כרטיס:"
              {...form.getInputProps('cardNumber')}
              mb="md"
            />
          </Grid.Col>

          <Grid.Col span={3} p={'0.5rem'}>
            <Select
              label="תוקף כרטיס:"
              data={years}
              {...form.getInputProps('expiryYear')}
            />
          </Grid.Col>

          <Grid.Col span={2}>
            <Select
              pt={'0.8rem'}
              data={months}
              {...form.getInputProps('expiryMonth')}
            />
          </Grid.Col>

          <Grid.Col span={6} p={'0.5rem'}>
            <TextInput
              label="3 ספרות בגב הכרטיס:"
              maxLength={3}
              minLength={3}
              {...form.getInputProps('cvv')}
              mb="md"
            />
          </Grid.Col>

          <Grid.Col span={6} p={'0.5rem'}>
            <TextInput
              label="תז בעל הכרטיס:"
              {...form.getInputProps('cardHolderId')}
              mb="md"
            />
          </Grid.Col>

          <Grid.Col span={6} p={'0.5rem'}>
            <TextInput
              label="שם בעל הכרטיס:"
              {...form.getInputProps('cardHolderName')}
              mb="md"
            />
          </Grid.Col>
        </Grid>
      </form>
    </Page>
  );
};

export default Pay;
